import { useEffect, useMemo, useState } from "react";
import { spotifyApi } from "../services/spotify";
import type { PreviewResponse, PreviewTrack, PreviewMatch } from "../types/setlists";
import { t as tx } from "../i18n";

// The review step both Sell Playlists uploads share: after a sale (the
// Balance upload) and ahead of one (Playlists for sale). The talent is the
// author of the tracklist — they confirm or swap the rows we weren't sure
// of, remove any song with one tap (their secret weapons), and add songs
// the file doesn't have. The server re-guards only the money side.

/** A song picked from search — a swap target or an added song. */
export type PickedSong = { id: string; name: string; artists: string; image?: string; isrc?: string | null };

// Per-row outcome. Flagged rows WITHOUT a decision are omitted (founder
// decision): the talent can always continue, but only confirmed tracks ship.
export type Decision = { kind: "kept" } | { kind: "dropped" } | { kind: "swapped"; track: PickedSong };

/** One row of the tracklist as /setlists/deliver and /setlists/shop take it. */
export type ReviewedTrack = {
  order: number;
  title: string;
  artist: string;
  spotifyId: string | null;
  image: string | null;
  startTime: string | null;
  isrc: string | null;
};

/** What a parked draft keeps so the review comes back as the talent left it. */
export type ReviewState = { decisions: Record<number, Decision>; added: PickedSong[] };

// The server's per-setlist ceiling (MAX_DELIVER_TRACKS) — the + button
// hides at it rather than letting the server trim the end of the list.
export const MAX_REVIEW_TRACKS = 250;

export const artistNames = (m?: PreviewMatch | null): string =>
  (m?.artists || [])
    .map((a) => (typeof a === "string" ? a : a?.name || ""))
    .filter(Boolean)
    .join(", ");

/** The list the server receives: kept + confirmed rows in file order, then
 *  the added songs. Also what a parked draft's row counts, so the two agree. */
export function reviewedTracks(tracks: PreviewTrack[], { decisions, added }: ReviewState): ReviewedTrack[] {
  const fromFile = tracks
    .filter((t) => {
      const d = decisions[t.order];
      if (d?.kind === "dropped") return false;
      // Unverified rows are omitted — never ship a guess the talent didn't confirm.
      if (t.grade !== "high" && !d) return false;
      return true;
    })
    .sort((a, b) => a.order - b.order)
    .map((t): ReviewedTrack => {
      const d = decisions[t.order];
      if (d?.kind === "swapped") {
        return { order: t.order, title: d.track.name, artist: d.track.artists, spotifyId: d.track.id, image: d.track.image || null, startTime: t.startTime || null, isrc: d.track.isrc || null };
      }
      if (t.match) {
        return { order: t.order, title: t.match.name, artist: artistNames(t.match), spotifyId: t.match.id, image: t.match.image || null, startTime: t.startTime || null, isrc: t.match.isrc || null };
      }
      // No match kept — the file's own words, no link/art.
      return { order: t.order, title: t.title, artist: t.artist, spotifyId: null, image: null, startTime: t.startTime || null, isrc: null };
    });
  // Added songs play after the set: numbered on from the file's last row,
  // no start time (they weren't in the night's history).
  const last = tracks.reduce((m, t) => Math.max(m, t.order), 0);
  const extra = added.map((s, i): ReviewedTrack => ({ order: last + i + 1, title: s.name, artist: s.artists, spotifyId: s.id, image: s.image || null, startTime: null, isrc: s.isrc || null }));
  return [...fromFile, ...extra];
}

type SearchHit = { id: string; name: string; artists: { name: string }[]; image: string; isrc?: string | null };

export function useSetlistReview(preview: PreviewResponse | null) {
  const [decisions, setDecisions] = useState<Record<number, Decision>>({});
  const [added, setAdded] = useState<PickedSong[]>([]);

  // The search sheet: swapping one row, or adding a new song.
  const [picker, setPicker] = useState<{ mode: "swap"; track: PreviewTrack } | { mode: "add" } | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchHit[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  // The query the shown results answer — "No songs found" only once the
  // search for what's typed has actually come back empty.
  const [answered, setAnswered] = useState<string | null>(null);

  const tracks = useMemo(() => preview?.tracks || [], [preview]);
  const flagged = useMemo(() => tracks.filter((t) => t.grade !== "high"), [tracks]);
  const autoMatched = useMemo(() => tracks.filter((t) => t.grade === "high"), [tracks]);
  const unresolvedFlagged = flagged.filter((t) => !decisions[t.order]);

  const finalTracks = useMemo(() => reviewedTracks(tracks, { decisions, added }), [tracks, decisions, added]);

  const removedCount = tracks.filter((t) => decisions[t.order]?.kind === "dropped").length;
  const canAdd = finalTracks.length < MAX_REVIEW_TRACKS;

  const setDecision = (order: number, d: Decision) => setDecisions((prev) => ({ ...prev, [order]: d }));
  const clearDecision = (order: number) =>
    setDecisions((prev) => {
      const next = { ...prev };
      delete next[order];
      return next;
    });
  const removeAdded = (id: string) => setAdded((prev) => prev.filter((s) => s.id !== id));

  const openSwap = (t: PreviewTrack) => {
    setPicker({ mode: "swap", track: t });
    setQuery(`${t.title} ${t.artist}`.trim());
    setResults([]);
    setSearchError(null);
    setAnswered(null);
  };
  const openAdd = () => {
    if (!canAdd) return;
    setPicker({ mode: "add" });
    setQuery("");
    setResults([]);
    setSearchError(null);
    setAnswered(null);
  };
  const closePicker = () => setPicker(null);

  const pick = (hit: SearchHit) => {
    const song: PickedSong = { id: hit.id, name: hit.name, artists: hit.artists.map((a) => a.name).join(", "), image: hit.image, isrc: hit.isrc || null };
    if (picker?.mode === "swap") setDecision(picker.track.order, { kind: "swapped", track: song });
    // A song already on the list isn't added twice.
    else if (picker?.mode === "add" && !finalTracks.some((t) => t.spotifyId === song.id)) setAdded((prev) => [...prev, song]);
    setPicker(null);
  };
  const dropPickerTrack = () => {
    if (picker?.mode === "swap") setDecision(picker.track.order, { kind: "dropped" });
    setPicker(null);
  };

  // Debounced search — the same /spotify/search the request page fans use.
  useEffect(() => {
    if (!picker) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const data = await spotifyApi.search(q, ctrl.signal);
        setResults(
          (data.tracks || []).slice(0, 8).map((tr) => ({ id: tr.id, name: tr.name, artists: tr.artists || [], image: tr.image || "", isrc: tr.isrc || null }))
        );
        setSearchError(null);
        setAnswered(q);
      } catch (e: any) {
        if (e?.name !== "CanceledError" && e?.name !== "AbortError") {
          setSearchError(tx("setlistUploadFlow.songSearchIsTemporarily"));
        }
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [query, picker]);

  const reset = (state?: Partial<ReviewState> | null) => {
    setDecisions(state?.decisions || {});
    setAdded(Array.isArray(state?.added) ? state!.added : []);
    setPicker(null);
  };

  return {
    preview,
    flagged,
    autoMatched,
    unresolvedFlagged,
    decisions,
    added,
    finalTracks,
    removedCount,
    canAdd,
    setDecision,
    clearDecision,
    removeAdded,
    picker,
    query,
    setQuery,
    results,
    searchError,
    noResults: !!picker && answered !== null && answered === query.trim() && results.length === 0 && !searchError,
    openSwap,
    openAdd,
    closePicker,
    pick,
    dropPickerTrack,
    reset,
    snapshot: { decisions, added } as ReviewState,
  };
}

export type SetlistReview = ReturnType<typeof useSetlistReview>;
